import type { AuditLogEntry } from '../api/access-control.client'

export type AccessHistoryKind = 'rolesUpdated' | 'companyAccess' | 'other'

export interface AccessHistoryItem {
  id: string
  timestamp: string
  actor: string
  kind: AccessHistoryKind
  title: string
  /** Roles before the change; null for entries that aren't role changes. */
  fromRoles: string[] | null
  /** Implied role on the "from" side (user had no granted role). */
  fromImplied: string | null
  /** Roles after the change. */
  roles: string[]
  /** Implied role on the "to" side (all granted roles removed). */
  toImplied: string | null
  reason: string | null
  /** Shown when the entry has no structured role data (older/other entries). */
  description: string | null
}

function stringArray(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((v) => typeof v === 'string')
    ? value
    : null
}

function impliedName(value: unknown): string | null {
  if (value && typeof value === 'object' && 'name' in value) {
    const { name } = value as { name: unknown }
    return typeof name === 'string' ? name : null
  }
  return null
}

// Before role names were logged, the drawer save only recorded
// "User access updated for user <id>" + a role count. The id is redundant in
// the user's own history, so summarise what is known instead.
function describeLegacyEntry(entry: AuditLogEntry): string {
  if (entry.action !== 'RBAC_USER_ACCESS_UPDATED') {
    return entry.description
  }
  const roleCount = entry.additionalData?.roleCount
  const assigned =
    typeof roleCount === 'number'
      ? `${roleCount} role${roleCount === 1 ? '' : 's'} assigned. `
      : ''
  return `${assigned}Role details weren't recorded for this change.`
}

const COMPANY_ACTION_TITLES: Record<string, string> = {
  RBAC_USER_COMPANIES_SYNCED: 'Company access granted',
  RBAC_USER_COMPANIES_REVOKED: 'Company access revoked',
}

/**
 * Turns an RBAC activity-log entry into a timeline item. Role-change entries
 * (RBAC_USER_ACCESS_UPDATED / RBAC_USER_ROLES_SYNCED) carry previous/new role
 * names and, when the user had no granted role, the implied role — rendered as
 * "Implied: X → new roles". Entries without that data fall back to their
 * description.
 */
export function toAccessHistoryItem(entry: AuditLogEntry): AccessHistoryItem {
  const data = entry.additionalData ?? {}
  const base = {
    id: String(entry.id),
    timestamp: entry.timestamp,
    actor: entry.actorName || entry.actorId,
    reason:
      typeof data.operatorReason === 'string' ? data.operatorReason : null,
  }

  const companyTitle = COMPANY_ACTION_TITLES[entry.action]
  if (companyTitle) {
    return {
      ...base,
      kind: 'companyAccess',
      title: companyTitle,
      fromRoles: null,
      fromImplied: null,
      roles: [],
      toImplied: null,
      description: entry.description,
    }
  }

  const previous = stringArray(data.previousRoleNames)
  const next = stringArray(data.roleNames)
  if (!previous || !next) {
    return {
      ...base,
      kind: 'other',
      title: 'Access updated',
      fromRoles: null,
      fromImplied: null,
      roles: [],
      toImplied: null,
      description: describeLegacyEntry(entry),
    }
  }

  const fromImplied =
    previous.length === 0 ? impliedName(data.previousImpliedRole) : null
  const toImplied = next.length === 0 ? impliedName(data.impliedRole) : null
  return {
    ...base,
    kind: 'rolesUpdated',
    title: 'Roles updated',
    fromRoles: previous,
    fromImplied,
    roles: next,
    toImplied,
    description: null,
  }
}
